import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from './ui/button';
import { Cookie } from 'lucide-react';

const CONSENT_KEY = 'cookie-consent';

const applyConsent = (choice) => {
  try {
    if (choice === 'accepted') {
      window.posthog && window.posthog.opt_in_capturing();
      window.posthog && window.posthog.capture('$pageview');
      // Consent Mode v2: grant all signals so GA4 and Google Ads attribution work fully.
      window.gtag && window.gtag('consent', 'update', {
        ad_storage: 'granted',
        ad_user_data: 'granted',
        ad_personalization: 'granted',
        analytics_storage: 'granted',
      });
    } else {
      window.posthog && window.posthog.opt_out_capturing();
      window.gtag && window.gtag('consent', 'update', {
        ad_storage: 'denied',
        ad_user_data: 'denied',
        ad_personalization: 'denied',
        analytics_storage: 'denied',
      });
    }
  } catch (e) {
    // Analytics libraries not loaded yet; consent is still persisted below.
  }
};

export const CookieConsent = () => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const stored = localStorage.getItem(CONSENT_KEY);
    if (stored === 'accepted' || stored === 'rejected') {
      applyConsent(stored);

      const reopenApplied = () => setVisible(true);
      window.addEventListener('open-cookie-preferences', reopenApplied);
      return () => window.removeEventListener('open-cookie-preferences', reopenApplied);
    }

    // No stored choice: hold the banner back until the visitor has engaged
    // (scrolled or clicked through), with a gentle timeout fallback. This keeps
    // ad visitors from being greeted by a wall on landing. No non-essential
    // cookies or analytics run before consent, so this remains compliant.
    let cleaned = false;
    const reveal = () => {
      if (cleaned) return;
      cleaned = true;
      cleanup();
      setVisible(true);
    };

    const onScroll = () => {
      if (window.scrollY > 300) reveal();
    };

    const timer = setTimeout(reveal, 12000);

    const cleanup = () => {
      clearTimeout(timer);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('click', onInteract);
    };

    const onInteract = (e) => {
      // Reveal once they click through to a link or button (engaged intent)
      if (e.target.closest && e.target.closest('a, button')) reveal();
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('click', onInteract);

    const reopen = () => setVisible(true);
    window.addEventListener('open-cookie-preferences', reopen);

    return () => {
      cleanup();
      window.removeEventListener('open-cookie-preferences', reopen);
    };
  }, []);

  const choose = (choice) => {
    localStorage.setItem(CONSENT_KEY, choice);
    applyConsent(choice);
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      data-testid="cookie-consent-banner"
      className="fixed bottom-0 left-0 right-0 z-[100] bg-[#0a0a0a] border-t border-gray-700 px-4 py-3 sm:px-6 sm:py-5"
    >
      <div className="max-w-5xl mx-auto flex flex-col gap-3 sm:gap-4 md:flex-row md:items-center md:justify-between">
        <div className="flex items-start gap-3">
          <Cookie className="h-5 w-5 text-[#F5F5DC] mt-0.5 flex-shrink-0" />
          <p className="sm:hidden text-[13px] text-white leading-snug" data-testid="cookie-consent-text-mobile">
            We use essential cookies. Analytics stay off until you accept.{' '}
            <Link to="/cookies" className="text-[#F5F5DC] underline">Cookies</Link> and{' '}
            <Link to="/privacy" className="text-[#F5F5DC] underline">Privacy</Link>.
          </p>
          <p className="hidden sm:block text-sm text-white leading-relaxed">
            We use essential cookies to run this site and, with your permission, privacy-friendly analytics to understand
            how it is used. Analytics stay switched off until you accept. Read our{' '}
            <Link to="/cookies" className="text-[#F5F5DC] underline hover:no-underline">Cookies Policy</Link> and{' '}
            <Link to="/privacy" className="text-[#F5F5DC] underline hover:no-underline">Privacy Policy</Link>.
          </p>
        </div>
        <div className="grid grid-cols-2 sm:flex gap-3 flex-shrink-0">
          <Button
            data-testid="cookie-reject-btn"
            variant="outline"
            className="border-gray-600 text-white hover:bg-white/10 rounded-full h-10 px-3 text-[13px] sm:text-sm"
            onClick={() => choose('rejected')}
          >
            Reject non-essential
          </Button>
          <Button
            data-testid="cookie-accept-btn"
            className="bg-[#F5F5DC] text-black hover:bg-[#e8e8c8] rounded-full h-10 px-3 text-[13px] sm:text-sm"
            onClick={() => choose('accepted')}
          >
            Accept all
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CookieConsent;
